import { useState, type FormEvent } from 'react'
import { api } from '@/lib/api.ts'
import { useAuth } from '@/state/AuthContext.tsx'
import { ApiError } from '@/lib/api.ts'
import { Vine, Bloom, LeafDivider } from '@/components/Botanical.tsx'

type Mode = 'login' | 'signup' | 'forgot'

export default function Welcome() {
  const { login, signup } = useAuth()
  const [mode, setMode] = useState<Mode>('login')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** Set once a reset has been asked for, whatever the outcome. */
  const [sent, setSent] = useState<'email' | 'unavailable' | null>(null)

  function switchTo(next: Mode) {
    setMode(next)
    setError(null)
    setSent(null)
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)
    setBusy(true)
    const data = new FormData(event.currentTarget)
    const email = String(data.get('email') ?? '')
    const password = String(data.get('password') ?? '')

    try {
      if (mode === 'forgot') {
        // Always reports success — see the note on the endpoint.
        const { delivery } = await api.auth.forgot(email)
        setSent(delivery)
        setBusy(false)
        return
      }
      if (mode === 'login') {
        await login(email, password)
      } else {
        await signup({
          email,
          password,
          displayName: String(data.get('displayName') ?? ''),
          inviteCode: String(data.get('inviteCode') ?? ''),
        })
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong.')
      setBusy(false)
    }
  }

  return (
    <div className="relative grid min-h-screen items-center overflow-hidden px-6 py-16">
      <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
        <Vine tone="var(--c-sage)" className="absolute -left-10 -top-10 h-[85vh] w-auto opacity-30 animate-sway" />
        <Vine tone="var(--c-clay)" className="absolute -right-12 bottom-0 h-[70vh] w-auto scale-x-[-1] opacity-25 animate-sway" style={{ animationDelay: '-5s' }} />
        <Bloom tone="var(--c-terracotta)" className="absolute left-[18%] top-[12%] h-14 w-14 opacity-30 animate-drift" />
      </div>

      <div className="mx-auto w-full max-w-[27rem] animate-unfurl">
        <div className="text-center">
          <p className="eyebrow">A quiet place for two</p>
          <h1 className="mt-4 font-display text-[3.1rem] leading-[0.98]" style={{ fontVariationSettings: "'SOFT' 70, 'WONK' 1" }}>
            Two to<br />Three
          </h1>
          <LeafDivider tone="var(--c-sage)" className="mx-auto mt-6 h-5 w-40 opacity-55" />
          <p className="prose-soft mx-auto mt-6 max-w-[24rem] text-[0.97rem]">
            {mode === 'login' && 'Everything is where you left it.'}
            {mode === 'signup' && 'Make yourself an account. You will need the invite code.'}
            {mode === 'forgot' && 'Tell us the address you signed up with and a link will follow.'}
          </p>
        </div>

        <form onSubmit={onSubmit} className="sheet mt-9 space-y-3.5 p-7">
          {mode === 'signup' && (
            <label className="block">
              <span className="eyebrow">Your name</span>
              <input name="displayName" required autoComplete="name" placeholder="What should we call you?" className="field mt-1.5" />
            </label>
          )}

          <label className="block">
            <span className="eyebrow">Email</span>
            <input name="email" type="email" required autoComplete="email" placeholder="you@example.com" className="field mt-1.5" />
          </label>

          {mode !== 'forgot' && (
          <label className="block">
            <span className="eyebrow">Password</span>
            <input
              name="password"
              type="password"
              required
              minLength={mode === 'signup' ? 10 : undefined}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              placeholder={mode === 'signup' ? 'At least 10 characters' : '••••••••••'}
              className="field mt-1.5"
            />
          </label>
          )}

          {mode === 'signup' && (
            <label className="block">
              <span className="eyebrow">Invite code</span>
              <input name="inviteCode" required placeholder="The one you both agreed on" className="field mt-1.5" />
            </label>
          )}

          {error && (
            <p role="alert" className="rounded-leaf border border-clay/40 bg-blush/25 px-4 py-2.5 text-[0.85rem] text-terracotta">
              {error}
            </p>
          )}

          {sent && (
            <p className="rounded-leaf border border-sage/40 bg-parchment/50 px-4 py-3 text-[0.85rem] text-quiet">
              {sent === 'email'
                ? 'If that address is one of ours, a link is on its way. It works once, and only for the next three quarters of an hour.'
                : 'Email is not switched on for this site yet, so a link cannot be sent. Ask the other one of you to sort it out.'}
            </p>
          )}

          <button type="submit" disabled={busy || Boolean(sent)} className="btn-primary mt-2 w-full">
            {busy ? 'One moment…'
              : mode === 'login' ? 'Come in'
              : mode === 'signup' ? 'Create my account'
              : sent ? 'Sent' : 'Send me a link'}
          </button>

          {mode === 'login' && (
            <p className="pt-1 text-center">
              <button
                type="button"
                onClick={() => switchTo('forgot')}
                className="text-[0.8rem] text-whisper underline decoration-linen underline-offset-4 transition-colors hover:text-fern hover:decoration-sage"
              >
                Forgotten your password?
              </button>
            </p>
          )}
        </form>

        <p className="mt-6 text-center text-[0.85rem] text-whisper">
          {mode === 'login' ? 'Not set up yet?' : 'Remembered it?'}{' '}
          <button
            onClick={() => switchTo(mode === 'login' ? 'signup' : 'login')}
            className="text-fern underline decoration-sage/40 underline-offset-4 transition-colors hover:decoration-sage"
          >
            {mode === 'login' ? 'Create an account' : 'Sign in instead'}
          </button>
        </p>
      </div>
    </div>
  )
}
